import { formatDate, formatAmount } from "@/lib/api";
import { PolicyTypeBadge } from "@/components/PolicyTypeBadge";
import { ExternalLink } from "lucide-react";

const POLICY_STATUS_CLASSES = {
  attiva: "bg-emerald-50 border-emerald-200 text-emerald-700",
  scaduta: "bg-red-50 border-red-200 text-red-700",
  sospesa: "bg-amber-50 border-amber-200 text-amber-700",
  disdetta: "bg-slate-100 border-slate-300 text-slate-600",
};
const policyStatusClass = (s) => POLICY_STATUS_CLASSES[s] || "bg-blue-50 border-blue-200 text-blue-700";

/**
 * Read-only table of policies linked to a client, shown inline in the client edit form.
 * Props:
 *  - policies: array of policy objects
 *  - onOpenPolicy?: (policy) => void  — se fornito, abilita il click su ogni riga per aprire la polizza
 */
export function ClientPoliciesSection({ policies, onOpenPolicy }) {
  const clickable = typeof onOpenPolicy === "function";
  return (
    <div className="col-span-2 border-t border-slate-200 pt-4 mt-2" data-testid="client-policies-section">
      <div className="text-sm font-semibold flex items-center gap-2">
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
        Polizze del cliente
        <span className="text-xs font-normal text-slate-500">({policies.length})</span>
        {clickable && policies.length > 0 && (
          <span className="text-[10px] font-normal text-slate-400 ml-1">— clicca una riga per aprire la polizza</span>
        )}
      </div>
      {policies.length === 0 ? (
        <p className="text-xs text-slate-400 mt-2">Nessuna polizza associata.</p>
      ) : (
        <div className="mt-2 border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-500 uppercase tracking-wide text-[10px]">
              <tr>
                <th className="text-left px-3 py-2">N° polizza</th>
                <th className="text-left px-3 py-2">Tipo</th>
                <th className="text-left px-3 py-2">Compagnia</th>
                <th className="text-left px-3 py-2">Veicolo</th>
                <th className="text-left px-3 py-2">Scadenza</th>
                <th className="text-right px-3 py-2">Premio</th>
                <th className="text-left px-3 py-2">Stato</th>
                {clickable && <th className="text-right px-3 py-2 w-8" />}
              </tr>
            </thead>
            <tbody data-testid="client-policies-list">
              {policies.map((p) => (
                <tr
                  key={p.id}
                  className={`border-t border-slate-100 ${clickable ? "cursor-pointer hover:bg-blue-50/60 transition-colors" : ""}`}
                  onClick={clickable ? () => onOpenPolicy(p) : undefined}
                  data-testid={`client-policy-row-${p.id}`}
                  title={clickable ? "Apri scheda polizza" : undefined}
                >
                  <td className="px-3 py-2 font-mono">{p.policy_number}</td>
                  <td className="px-3 py-2"><PolicyTypeBadge code={p.type} size="xs" testid={`client-pol-type-${p.id}`} /></td>
                  <td className="px-3 py-2 text-slate-600">{p.company || "—"}</td>
                  <td className="px-3 py-2 text-slate-600" data-testid={`client-pol-vehicle-${p.id}`}>
                    {(() => {
                      const brand = (p.vehicle_brand || "").trim();
                      const model = (p.vehicle_model || "").trim();
                      const plate = (p.vehicle_plate || "").trim().toUpperCase();
                      if (!brand && !model && !plate) return <span className="text-slate-300">—</span>;
                      const vehLabel = [brand, model].filter(Boolean).join(" ");
                      return (
                        <div className="leading-tight">
                          {vehLabel && <div className="truncate max-w-[180px]" title={vehLabel}>{vehLabel}</div>}
                          {plate && <div className="font-mono text-xs font-semibold text-slate-900 mt-0.5">{plate}</div>}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-3 py-2 tabular-nums">{p.end_date ? formatDate(p.end_date) : "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{p.premium ? `€ ${formatAmount(p.premium)}` : "—"}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] border ${policyStatusClass(p.status)}`}>{p.status}</span>
                  </td>
                  {clickable && (
                    <td className="px-3 py-2 text-right text-blue-600">
                      <ExternalLink className="h-3.5 w-3.5 inline" />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
